/**
 * 用户状态：token / 用户信息 / 角色 / 权限 / 菜单
 */
import {
  login as loginApi,
  logout as logoutApi,
  getUserInfo as getUserInfoApi
} from '@/utils/systemApis'
import {
  getToken,
  setToken,
  removeToken
} from '@/utils/auth'

/**
 * 兼容不同后端字段命名，统一取出数组
 */
function pickArray(source, keys) {
  for (const key of keys) {
    if (Array.isArray(source?.[key])) return source[key]
  }
  return []
}

function normalizeUserInfo(raw) {
  const payload = raw?.data || raw || {}
  // 有些后端把用户信息包在 user 字段里；若本身就是一个用户对象则直接使用
  const user = payload.user || payload.userInfo || payload

  const roles = pickArray(payload, ['roles', 'roleCodes', 'roleList'])
    .map(item =>
      typeof item === 'string' ? item : item.roleCode || item.code || item.roleKey || ''
    )
    .filter(Boolean)

  const permissions = pickArray(payload, ['permissions', 'perms', 'permissionList'])
    .map(item => (typeof item === 'string' ? item : item.perms || item.code || ''))
    .filter(Boolean)

  // 菜单可能挂在根节点，也可能挂在 user 上
  let menus = pickArray(payload, ['menus', 'menuList', 'menuTree'])
  if (!menus.length) {
    menus = pickArray(user, ['menus', 'menuList', 'menuTree'])
  }

  return { user, roles, permissions, menus }
}

const state = () => ({
  token: getToken(),
  userInfo: {},
  roles: [],
  permissions: [],
  menus: [],
  // 是否已拉取过用户信息，避免重复请求
  loaded: false
})

const getters = {
  isLogin: state => !!state.token,
  userName: state =>
    state.userInfo?.nickName ||
    state.userInfo?.nickname ||
    state.userInfo?.realName ||
    state.userInfo?.userName ||
    state.userInfo?.username ||
    '用户',
  avatar: state => state.userInfo?.avatar || state.userInfo?.avatarUrl || '',
  // 超级管理员拥有所有权限
  isAdmin: state =>
    state.roles.includes('admin') ||
    state.roles.includes('super_admin') ||
    state.roles.includes('ROLE_ADMIN') ||
    state.permissions.includes('*:*:*'),
  /**
   * 权限校验：支持单个字符串或数组（任一命中即可）
   * 未配置权限的页面默认放行
   */
  hasPermission: state => required => {
    if (!required) return true
    const list = Array.isArray(required) ? required : [required]
    if (!list.length) return true

    const isAdmin =
      state.roles.includes('admin') ||
      state.roles.includes('super_admin') ||
      state.roles.includes('ROLE_ADMIN') ||
      state.permissions.includes('*:*:*')

    if (isAdmin) return true
    return list.some(code => state.permissions.includes(code))
  }
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_USER_INFO(state, info) {
    state.userInfo = info
  },
  SET_ROLES(state, roles) {
    state.roles = roles
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions
  },
  SET_MENUS(state, menus) {
    state.menus = menus
  },
  SET_LOADED(state, loaded) {
    state.loaded = loaded
  },
  RESET(state) {
    state.token = ''
    state.userInfo = {}
    state.roles = []
    state.permissions = []
    state.menus = []
    state.loaded = false
  }
}

const actions = {
  /**
   * 登录：成功后保存 token
   */
  async login({ commit }, { username, password }) {
    const res = await loginApi({ username, password })
    const data = res?.data || {}

    // 兼容 token / accessToken 两种字段
    const token = data.token || data.accessToken || data.access_token
    if (!token) {
      throw new Error('登录接口未返回 token')
    }

    setToken(token)
    commit('SET_TOKEN', token)

    // 登录接口已返回部分用户信息，先填充避免页面闪烁
    if (data.userId || data.username || data.nickname) {
      commit('SET_USER_INFO', {
        userId: data.userId,
        username: data.username,
        userName: data.username,
        nickname: data.nickname,
        nickName: data.nickname
      })
    }

    return res
  },

  /**
   * 拉取用户信息（含菜单与角色）
   */
  async getUserInfo({ commit, state }) {
    const res = await getUserInfoApi()
    const { user, roles, permissions, menus } = normalizeUserInfo(res)

    commit('SET_USER_INFO', user || {})
    commit('SET_ROLES', roles)
    commit('SET_PERMISSIONS', permissions)
    commit('SET_MENUS', menus)
    commit('SET_LOADED', true)

    return { user, roles, permissions, menus }
  },

  /**
   * 退出登录：先调接口，失败也要清理本地状态
   */
  async logout({ commit }) {
    try {
      await logoutApi()
    } catch (e) {
      // 后端异常不影响前端登出
    } finally {
      removeToken()
      commit('RESET')
    }
  },

  /**
   * 本地清理（token 失效时使用）
   */
  resetState({ commit }) {
    removeToken()
    commit('RESET')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
